import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { UserRole, UtilityHubOrganisation } from '@auth/models/utility-hub-identity';
import * as _moment from 'moment';
import { default as _rollupMoment } from 'moment';
import { Observable } from 'rxjs';
import { getAccountName, UtilityInformation } from '../models/utility-information';
import { UtilityRateCode } from '../models/utility-rate-code';
import { RateCodesByUtilityShort, UtilitiesByZipCode, UtilityAccount } from '../models/UtilityAccount';
import { ConfigService } from './config.service';
import { CommodityType } from '../enums/commodity-type';
import { QuoteRequestState } from '../models/quote-request-state';
import { SupplierUtilityModel } from '../models/supplier-utility-model';

const moment = _rollupMoment || _moment;

@Injectable({
  providedIn: 'root',
})
export class UtilityAccountService {
  public listOfUtilitiesByZipCode: UtilitiesByZipCode[] = [];
  public listofRateCodesByUtilityShort: RateCodesByUtilityShort[] = [];
  private completedQuotesThatDontRequireStartValidation: string[] = [
    'PendingEnrollment', 'PartiallyEnrolled',
    'Enrolled', 'QuoteEnrolled',
    'ContractLive', 'ContractExpiring',
    'ContractDropPending', 'ContractDropped',
    'SubmissionSuccessful', 'SubmissionFailed',
    'PartiallySubmitted'
  ];

  constructor(private configService: ConfigService, private httpClient: HttpClient) {}

  public addRateCodes(utilityShort: string, utilityRateCodes: UtilityRateCode[]): void {
    const utility = this.listofRateCodesByUtilityShort.find((utility) => utility?.utilityShort === utilityShort);

    if (utility) {
      utility.utilityRateCodes = utilityRateCodes;
    } else {
      this.listofRateCodesByUtilityShort.push({ utilityShort, utilityRateCodes });
    }
  }

  private getRateCodes(utilityShort: string): UtilityRateCode[] {
    const utility = this.listofRateCodesByUtilityShort
        .find(utility => utility.utilityShort === utilityShort)
        ?.utilityRateCodes || [];
    
    return utility;
}

  public isErcotIso(iso: string): boolean {
    return iso?.toLowerCase() === 'ercot';
  }

  public requestConsumptionDataForAccounts(quoteRequestId: number, utilityAccounts: UtilityAccount[]): Observable<any[]> {
    const path = this.configService.getApiPath(`meterData/RequestMeterData`);
    const body = {
      quoteRequestId: quoteRequestId,
      utilityAccountNumbers: utilityAccounts.map((x) => x.utilityAccountNumber),
    };
    return this.httpClient.post<any[]>(path, body);
  }

  public validateAll(
    utilityAccounts: UtilityAccount[],
    supplierUtilityInformation: SupplierUtilityModel[],
    quoteRequestState: QuoteRequestState,
    utilityHubOrganistion: UtilityHubOrganisation,
    madeRequestForUtilityInformation: { [key: string]: boolean },
    madeRequestForRatecodes: { [key: string]: boolean },
    isReadOnly: boolean,
    validateRateCode: boolean = true,
    validateStartMonth: boolean = true,
    mandatoryLoadProfile: string,
    mandatoryStartDate: string,
    validateKeyAndMeterNumber: boolean = false,
  ): { 
      valid: boolean; 
      entities: ClassValidation[]; 
   } {
    const valCol = { valid: true, entities: [] };
    utilityAccounts?.forEach((ua) => {
      const hasMadeRequestForUtilityInformation = madeRequestForUtilityInformation[ua?.utilityAccountNumber];
      const hasMadeRequestForRatecodes = madeRequestForRatecodes[ua?.utilityDetails?.utilityShort];

      const val = this.validate(
        ua, 
        supplierUtilityInformation,
        quoteRequestState,
        hasMadeRequestForUtilityInformation,
        hasMadeRequestForRatecodes,
        isReadOnly,
        utilityAccounts, 
        utilityHubOrganistion, 
        validateRateCode, 
        validateStartMonth, 
        mandatoryLoadProfile, 
        mandatoryStartDate, 
        validateKeyAndMeterNumber,
      );

      valCol.valid = valCol.valid && val.valid;
      valCol.entities.push(val);
    });
    if (valCol.entities.length === 0) {
      valCol.valid = false;
    }
    return valCol;
  }

  public validateUniqueness(
    utilityAccountBeingAdded: UtilityAccount, 
    utilityAccounts: UtilityAccount[], 
    checkForAtleastOne: boolean, 
    validateKeyAndMeterNumber: boolean
  ): UtilityAccount {
    const val: ClassValidation = new ClassValidation();
    utilityAccounts.forEach((utilityAccount) => {
      const utilityInfo = this.findUtilityInformation(utilityAccount?.address?.zipCode, utilityAccount?.utilityDetails?.utilityShort);
      if (utilityInfo) {
        let isUnique = true;
        let isUniqueUan = true;
    
        if (validateKeyAndMeterNumber) {
          isUnique = checkForAtleastOne
            ? !utilityAccounts.some((x) =>
                x.meterNumber && utilityAccount.meterNumber && (x.utilityAccountNumber + x.meterNumber) === (utilityAccountBeingAdded.utilityAccountNumber + utilityAccountBeingAdded.meterNumber))
            : utilityAccounts.filter((x) =>
                x.meterNumber && utilityAccount.meterNumber && (x.utilityAccountNumber + x.meterNumber) === (utilityAccountBeingAdded.utilityAccountNumber + utilityAccountBeingAdded.meterNumber)).length <= 1;
    
          if (!utilityInfo.meterNumberIsMandatory) {
            isUniqueUan = checkForAtleastOne
              ? !utilityAccounts.some((x) =>
                  !x.meterNumber && !utilityAccount.meterNumber && x.utilityAccountNumber === utilityAccountBeingAdded.utilityAccountNumber)
              : utilityAccounts.filter((x) =>
                  !x.meterNumber && !utilityAccount.meterNumber && x.utilityAccountNumber === utilityAccountBeingAdded.utilityAccountNumber).length <= 1;
          }
    
        } else {
          isUniqueUan = checkForAtleastOne
            ? !utilityAccounts.some((x) => x.utilityAccountNumber === utilityAccountBeingAdded.utilityAccountNumber)
            : utilityAccounts.filter((x) => x.utilityAccountNumber === utilityAccountBeingAdded.utilityAccountNumber).length <= 1;
        }

        if (!isUnique) {
          val.valid = false;
          val.fields.meterNumber = new PropertyValidation({
            valid: false,
            errors: { duplicate: `This ${getAccountName(utilityInfo)} and Meter Number combination already exists` },
          });
        }
    
        if (!isUniqueUan) {
          val.valid = false;
          val.fields.utilityAccountNumber = new PropertyValidation({
            valid: false,
            errors: { duplicate: `This ${getAccountName(utilityInfo)} already exists` },
          });
        }
      }
    });
    
    utilityAccountBeingAdded.validationInfo = val;
    return utilityAccountBeingAdded;
  }
  
  public addUtilities(zipCode: string, utilityInformation: UtilityInformation[]): void {
    const utilityExists = this.listOfUtilitiesByZipCode.find((utility) => utility.zipCode === zipCode);

    if (!utilityExists) {
      const utility: UtilitiesByZipCode = {
        zipCode,
        utilityInformation
      };
      
      this.listOfUtilitiesByZipCode.push(utility);
    }
  }

  public findUtilities(zipCode: string): UtilityInformation[] {
    const listOfUtilities = this.listOfUtilitiesByZipCode.find((utility) => utility.zipCode === zipCode);
    const utilityInformation = listOfUtilities?.utilityInformation ?? [];
    return utilityInformation;
  }

  public findUtilityInformation(zipCode: string, utilityShort: string) {
    const utilityByZipCode = this.listOfUtilitiesByZipCode.find((utility) => utility.zipCode === zipCode);

    if (utilityByZipCode) {
      const utilityInformation = utilityByZipCode.utilityInformation.find((utilInfo) => utilInfo.utilityShort.toLowerCase() === utilityShort?.toLowerCase());
      return utilityInformation;
    }
  }

  public findRateCodes(utilityAccount: UtilityAccount): UtilityRateCode[] {
    const utilityShort = utilityAccount?.utilityDetails?.utilityShort ?? '';

    if (utilityShort) {
      const utility = this.listofRateCodesByUtilityShort.find((utility) => utility?.utilityShort === utilityShort);
      return utility?.utilityRateCodes ?? [];
    }
    
    return [];
  }
  
  public validate(
    ua: UtilityAccount,
    supplierUtilityInformation: SupplierUtilityModel[],
    quoteRequestState: QuoteRequestState,
    madeRequestForUtilityInformation: boolean,
    madeRequestForRatecodes: boolean,
    isReadOnly: boolean,
    utilityAccounts?: UtilityAccount[],
    utilityHubOrganisation?: UtilityHubOrganisation,
    validateRateCode: boolean = true,
    validateStartMonth: boolean = true,
    mandatoryLoadProfile: string = '',
    mandatoryStartDate: string = '',
    validateKeyAndMeterNumber: boolean = false,
  ) {
    const utilityInformation = this.findUtilityInformation(ua?.address?.zipCode, ua?.utilityDetails?.utilityShort);
    ua.validationInfo = null;
    const val: ClassValidation = new ClassValidation();

    if (!isReadOnly) {
      const isGas = quoteRequestState?.commodity === CommodityType.Gas;
      const rateCodes = this.getRateCodes(ua?.utilityDetails?.utilityShort);

      if (utilityInformation) {
        ua.utilityAccountNumber = ua.utilityAccountNumber?.trim() ?? '';

        const minLength = utilityInformation?.accountLength ?? 1;
        const maxLength = utilityInformation?.accountLengthMax ?? 50;
        const pattern = `^${utilityInformation?.accountNumberStartWith ?? '.*'}`;
        const meterNumberMaxLength = 50;
        const meterNumberPattern = "^[a-zA-Z0-9]*$";

        /**will always be false until a history is created to validate the combination of utilityAccountNumber and MeterNumber throughout the system, if in doubt talk to JC. */
        utilityInformation.meterNumberIsMandatory = false;

        const errors = [];

        if (ua.utilityAccountNumber?.length > maxLength) {
          errors.push(`${getAccountName(utilityInformation)} must not be more than ${maxLength} characters long`);
        }

        if(!ua.utilityAccountNumber) {
          errors.push(`Please enter a ${getAccountName(utilityInformation)}`);
        }

        if (ua.utilityAccountNumber && (ua.utilityAccountNumber?.length < minLength)) {
          errors.push(`${getAccountName(utilityInformation)} must not be less than ${minLength} characters long`);
        }

        if (!ua.utilityAccountNumber.match(pattern)) {
          errors.push(`${getAccountName(utilityInformation)} must start with ${utilityInformation?.accountNumberStartWith}`);
        }

        if (errors.length) {
          val.valid = false;
          val.fields.utilityAccountNumber = new PropertyValidation({
            valid: false,
            errors: errors,
          });
        }

        //Name Key / Service Key / Meter Number Validation
        if (validateKeyAndMeterNumber === true) {
          if (utilityInformation.meterNumberIsMandatory && !ua.meterNumber) {
            errors.push(`Please enter Meter Number`);
          }

          if (ua.meterNumber?.length > meterNumberMaxLength) {
            errors.push(`Meter Number must not be more than ${meterNumberMaxLength} characters long`);
          }

          if (ua.meterNumber?.length > 0 && !ua.meterNumber.match(meterNumberPattern)) {
            errors.push(`Meter number can contain only letters and numbers`);
          }

          if (errors.length) {
            val.valid = false;
            val.fields.meterNumber = new PropertyValidation({
              valid: false,
              errors: errors,
            });
          }

          if (utilityInformation?.useNameKey) {
            const len = utilityInformation.keyLength;
            ua.nameKey = ua.nameKey?.trim() ?? '';

            if (ua.nameKey.length === 0) {
              errors.push(`Please enter a Name Key`);
            }
            else if (ua.nameKey.length !== len) {
              errors.push(`Name Key must be ${len} characters`);
            }

            if (errors.length) {
              val.valid = false;
              val.fields.nameKey = new PropertyValidation({
                valid: false,
                errors: errors,
              });
            }
          }

          if (utilityInformation?.useServiceNumber) {
            const len = utilityInformation.keyLength;
            ua.serviceKey = ua.serviceKey?.trim() ?? '';

            if (ua.serviceKey.length === 0) {
              errors.push(`Please enter a Service Key`);
            }
            else if (ua.serviceKey.length !== len) {
              errors.push(`Service Key must be ${len} characters`);
            }

            if (errors.length) {
              val.valid = false;
              val.fields.serviceKey = new PropertyValidation({
                valid: false,
                errors: errors,
              });
            }
          }
        }

        if (validateRateCode) {
          if (!ua?.rateCodeDetails?.rateCode || ua?.rateCodeDetails?.rateCode === '') {
            val.valid = false;
            val.fields.rateCode = new PropertyValidation({ valid: false, errors: { required: 'Please select a Load Profile' } });
          } else if (rateCodes?.length && rateCodes.findIndex(rateCode => rateCode.rateCode === ua?.rateCodeDetails.rateCode) < 0) {
            val.valid = false;
            val.fields.rateCode = new PropertyValidation({
              valid: false,
              errors: {
                invalidRateCode: `The rate code "${ua?.rateCodeDetails.rateCode}" was not found`,
              },
            });
          }
        }

        if(mandatoryLoadProfile?.length > 0)
        {
          if(ua.rateCodeDetails.rateCode !== mandatoryLoadProfile)
          {
            val.fields.rateCode = new PropertyValidation({
              valid: false,
              errors: {
                invalidRateCode: `Your Load Profile selection does not match the Load Profile selection in the Quote Configuration page (${mandatoryLoadProfile}). This could affect your prices. Please adjust your selection or perform a new matrix or custom quote`,
              },
            });
          }
        }

        if(!this.completedQuotesThatDontRequireStartValidation.includes(quoteRequestState?.status)){
        if (validateStartMonth) {
          const testDate = new Date();
          const date = new Date(ua.newContractStartDate);

          if (!ua.newContractStartDate || ua.newContractStartDate === '') {
            val.valid = false;
            val.fields.newContractStartDate = new PropertyValidation({
              valid: false,
              errors: { required: 'Please enter your New Contract Start Month as MMM-YYYY.' },
            });
          } else {
            var userTimezoneOffset = date.getTimezoneOffset() * 60000;
            if (userTimezoneOffset >= 0) userTimezoneOffset * -1;
            const contractDate = new Date(date.getTime() + userTimezoneOffset);

            if (contractDate.getFullYear() < testDate.getFullYear()) {
              val.valid = false;
              val.fields.newContractStartDate = new PropertyValidation({
                valid: false,
                errors: { required: 'Please select a date in the future' },
              });
              // getMonth is zero indexed! (Stupid..)
            } else if (contractDate.getMonth() + 1 < testDate.getMonth() + 1 && contractDate.getFullYear() <= testDate.getFullYear()) {
              val.valid = false;
              val.fields.newContractStartDate = new PropertyValidation({
                valid: false,
                errors: { required: 'Please select a date in the future' },
              });
            }
          }
        }
        }

        if(mandatoryStartDate?.length > 0) {
          const mandatoryDate = moment.utc(mandatoryStartDate);
          const userDate = moment.utc(ua.newContractStartDate);
          if(mandatoryDate.year() !== userDate.year()
          || mandatoryDate.month() !== userDate.month())
          {
            val.fields.newContractStartDate = new PropertyValidation({
              valid: false,
              errors: {
                invalidNewContractStartDate: `Your New Contract Start Date selection does not match the New Contract Start Date selection in the Quote Configuration page (${ mandatoryDate.month() + 1}-${mandatoryDate.year()}). This could affect your prices. Please adjust your selection or perform a new matrix or custom quote`,
              },
            });
          }
        }

        if (utilityAccounts) {
          const uniqueVal = this.validateUniqueness(ua, utilityAccounts, false, validateKeyAndMeterNumber);
          if (!uniqueVal.validationInfo?.valid && uniqueVal.validationInfo?.fields.utilityAccountNumber) {
            val.valid = false;
            val.fields.utilityAccountNumber = new PropertyValidation({
              valid: false,
              errors: { duplicate: `This ${getAccountName(utilityInformation)} already exists` },
            });
          }
          if (!uniqueVal.validationInfo?.valid && uniqueVal.validationInfo?.fields.meterNumber) {
            val.valid = false;
            val.fields.meterNumber = new PropertyValidation({
              valid: false,
              errors: { duplicate: `This ${getAccountName(utilityInformation)} and Meter Number combination already exists` },
            });
          }
        }

        if (validateRateCode) {
          // allow broker and broker manager to set I don't know in rate code should show error for sm
          if (ua?.rateCodeDetails?.rateCode === "I don't know" && utilityHubOrganisation.role !== UserRole.BrokerManager && utilityHubOrganisation.role !== UserRole.Broker) {
            val.valid = false;
            val.fields.rateCode = new PropertyValidation({
              valid: false,
              errors: {
                invalidRateCode: `Please select a Load Profile`,
              },
            });
          }
        }

        if (!ua.address?.zipCode) {
          val.valid = false;
          val.fields.zipCode = val.fields.zipCode = new PropertyValidation({
            valid: false,
            errors: {
              invalidzipCode: `Please enter a valid zipCode`,
            },
          });
        }

        if (!ua.utilityDetails?.utilityShort) {
          val.valid = false;
          val.fields.utilityShort = val.fields.utilityShort = new PropertyValidation({
            valid: false,
            errors: {
              invalidUtilityShort: `Please select a utility`,
            },
          });
        }

        if (!ua.utilityDetails?.zone && !isGas) {
          val.valid = false;
          val.fields.zone = val.fields.zone = new PropertyValidation({
            valid: false,
            errors: {
              invalidzone: `Please select a zone`,
            },
          });
        }

        if ((this.isErcotIso(ua.utilityDetails?.iso) && !ua.utilityDetails?.weatherZone) && !isGas) {
          val.valid = false;
          val.fields.weatherZone = val.fields.weatherZone = new PropertyValidation({
            valid: false,
            errors: {
              invalidzone: `Please select a weather zone`,
            },
          });
        }

      } else {
        if (!ua.address?.zipCode) {
          val.fields.zipCode = new PropertyValidation({
            valid: false,
            errors: { invalidZipCode: 'Please enter a valid zip code' },
          });
          val.valid = false;
        }
        
        if (!ua.utilityDetails?.utilityShort) {
          val.fields.utilityShort = new PropertyValidation({
            valid: false,
            errors: { invalidUtilityShort: 'Please select a utility' },
          });
          val.valid = false;
        }
    
        if (!ua.utilityDetails?.zone && !isGas) {
          val.fields.zone = new PropertyValidation({
            valid: false,
            errors: { invalidZone: 'Please select a zone' },
          });
          val.valid = false;
        }
    
        if ((this.isErcotIso(ua.utilityDetails?.iso) && !ua.utilityDetails?.weatherZone) && !isGas) {
          val.fields.weatherZone = new PropertyValidation({
            valid: false,
            errors: { invalidWeatherZone: 'Please select a weather zone' },
          });
          val.valid = false;
        }
        
        if ((!ua?.rateCodeDetails?.rateCode || ua?.rateCodeDetails?.rateCode === '') && validateRateCode) {
          val.valid = false;
          val.fields.rateCode = new PropertyValidation({ valid: false, errors: { required: 'Please select a Load Profile' } });
        }
    
        if ((!ua.newContractStartDate || ua.newContractStartDate === '') && validateStartMonth) {
          val.valid = false;
          val.fields.newContractStartDate = new PropertyValidation({
            valid: false,
            errors: { required: 'Please enter your New Contract Start Month as MMM-YYYY.' },
          });
        }
      }

      if (utilityAccounts?.length > 0) {
        const states = utilityAccounts.map(ua => ua.address?.state?.trim()).filter(state => state); 
        const uniqueStates = new Set(states);
      
        if (uniqueStates.size > 1) {
          val.fields.state = new PropertyValidation({
            valid: false,
            errors: { mismatchedStates: 'All utility accounts must come from the same state.' },
          });
          val.valid = false;
        }
      }

      this.validateZipCode(ua, val);
      
      if (madeRequestForUtilityInformation) {
        this.validateUtilityInformation(ua, val);
      }

      if (madeRequestForRatecodes) {
        this.validateUtilityAndRateCodes(ua, supplierUtilityInformation, utilityHubOrganisation, validateRateCode, rateCodes, val);
      }
    }
    
    ua.validationInfo = val;
    return val;
  }

  public validateZipCode(utilityAccount: UtilityAccount, val?: ClassValidation): ClassValidation {
    const validation = val ?? new ClassValidation();
    utilityAccount.validationInfo = null;
    const city = utilityAccount?.address?.city;
    const stateCode = utilityAccount?.address?.state;
    const zipCode = utilityAccount?.address?.zipCode;

    const isValidZipCode = this.isValidZipCode(zipCode);
    
    if (!city && !stateCode && zipCode && isValidZipCode) {
      validation.valid = false;
      validation.fields.zipCode = new PropertyValidation({
        valid: false,
        errors: { invalidZipCode: 'Invalid Zip Code. Please try again' },
      });
    } 

    if (zipCode && !isValidZipCode) {
      validation.valid = false;
      validation.fields.zipCode = new PropertyValidation({
        valid: false,
        errors: { invalid: 'Please enter a valid 5-digit ZIP code' },
      });
    }
 
    utilityAccount.validationInfo = validation;
    return validation;
  }  

  private isValidZipCode(zipCode: string): boolean {
    // Must be exactly 5 digits (0-9)
    const usZipPattern = /^\d{5}$/;
    return usZipPattern.test(zipCode?.trim());
  }

  public validateUtilityInformation(utilityAccount: UtilityAccount, val?: ClassValidation) {
    const validation = val ?? new ClassValidation();
    utilityAccount.validationInfo = null;
    
    const zipCode = utilityAccount?.address?.zipCode ?? '';
    const city = utilityAccount?.address?.city ?? '';
    const stateCode = utilityAccount?.address?.state ?? '';

    const ZIP_MIN_LENGTH = 5;
    const ZIP_MAX_LENGTH = 7;

    const isValidZipLength = zipCode.length >= ZIP_MIN_LENGTH && zipCode.length <= ZIP_MAX_LENGTH;
    const isValidZipCode = isValidZipLength && city && stateCode;

    const utilityInformation = this.findUtilities(zipCode);

    if (isValidZipCode && utilityInformation.length === 0) {
      validation.valid = false; 
      validation.fields.utilityShort = new PropertyValidation({
        valid: false,
        errors: { noUtilityInformation: `Sorry, but no utilities were found for this zip code.` },
      });
    }  
 
    utilityAccount.validationInfo = validation;
    return validation;
  }

  public validateUtilityAndRateCodes(
    utilityAccount: UtilityAccount, 
    supplierUtilityModel: SupplierUtilityModel[], 
    utilityHubOrganisation: UtilityHubOrganisation, 
    validateRateCode: boolean,
    utilityRateCodes?: UtilityRateCode[], 
    val?: ClassValidation
  ): ClassValidation {
      const validation = val ?? new ClassValidation();
      utilityAccount.validationInfo = null;

      const rateCodes = utilityRateCodes ?? [];
      const utility = utilityAccount?.utilityDetails?.utilityShort ?? '';
      const utilityUOM = utilityAccount?.utilityDetails?.uom ?? '';
  
      const existsInSupplierModel = supplierUtilityModel.some(supplier => supplier?.utility?.shortName.toLowerCase() === utility.toLowerCase());
  
      if (!existsInSupplierModel && utility) {
        validation.valid = false;
        validation.fields.utilityShort = new PropertyValidation({
            valid: false,
            errors: { unsupportedUtilityBySupplier: `Sorry, ${utilityHubOrganisation?.name} does not quote for this utility` },
        });
      } else if (utility && !utilityUOM) {
        validation.valid = false;
        validation.fields.utilityShort = new PropertyValidation({
            valid: false,
            errors: { utilityNotSupported: 'Sorry, the selected utility is not supported' },
        });
      }

      if (utility && rateCodes.length === 0 && validateRateCode) {
        validation.valid = false;
        validation.fields.rateCode = new PropertyValidation({
            valid: false,
            errors: { noUtilityRateCodes: 'Sorry, no load profiles were found for this utility' },
        });
      }
      
      utilityAccount.validationInfo = validation;
      return validation;
  }  

  public parsePastedData(pastedText, currentAccountCodes, utilInf?: UtilityInformation, validateKeyAndMeterNumber?: boolean) {
  pastedText = pastedText.split(',').join('\n').trim();
  const pastedArr = pastedText.split('\n').map((x) => x.split('\t'));
  const tmp: string[] = [];
  const result = new PasteResult();

  for (const pastedLine of pastedArr) {
    if (result.rowLimitExceeded) {
      break;
    }

    const row = this.extractInsertedData(pastedLine, utilInf, validateKeyAndMeterNumber);

    if (validateKeyAndMeterNumber === true && row.meterNumber != undefined) {
      if (
        result.newRows.some((newRow) => newRow.utilityAccountNumber + newRow.meterNumber === row.utilityAccountNumber + row.meterNumber) ||
        (currentAccountCodes.includes(row.utilityAccountNumber + row.meterNumber))
      ) {
        if (!tmp.includes(row.utilityAccountNumber + row.meterNumber)) {
          result.duplicates.push(row.utilityAccountNumber + (row.meterNumber ? ":" + row.meterNumber : ""));
          tmp.push(row.utilityAccountNumber + row.meterNumber);
        }
      } else {
        result.newRows.push(row);
      }
    } else {
      if (
        result.newRows.some((newRow) => newRow.utilityAccountNumber === row.utilityAccountNumber) ||
        currentAccountCodes.includes(row.utilityAccountNumber)
      ) {
        if (!result.duplicates.includes(row.utilityAccountNumber)) {
          result.duplicates.push(row.utilityAccountNumber);
        }
      } else {
        result.newRows.push(row);
      }
    }
  }
  return result;
}

private extractInsertedData(entity: string[], utilInf?: UtilityInformation, validateKeyAndMeterNumber?: boolean) {
  let utilAccCol: number = null;
  let meterNumberCol: number = null;
  let zipCodeCol: number = null; 
  let startDateCol: number = null;
  let keyCol: number = null;

  /*
  Code Summary:
  The following block of code outlines the structure for an Excel file and specifies the column positions for various fields. 
  It is designed to handle two scenarios based on the value of `validateKeyAndMeterNumber`.

  This block is commented out to serve as a reference for potential future functionality. 
  It can be reactivated if meter number and key validation need to be included in the Excel structure again.
  
  if (validateKeyAndMeterNumber === true) {
    utilAccCol = 0;
    meterNumberCol = 1;
    zipCodeCol = 2; 
    startDateCol = 3;
    keyCol = 4;
  } else {
    utilAccCol = 0;
    zipCodeCol = 1;
    startDateCol = 2;
  } 
  */

  utilAccCol = 0;
  zipCodeCol = 1;
  startDateCol = 2;

  const supNoPc: UtilityAccount = new UtilityAccount();
  supNoPc.address = {
    zipCode: "",
    city: "",
    state: "",
    country: "",
    addressLine1: "",
    quoteRequestId: null
  }

  if (validateKeyAndMeterNumber === true) {
    if (utilAccCol !== null && entity[utilAccCol]) {
      supNoPc.utilityAccountNumber = entity[utilAccCol].trim();
    }
    if (meterNumberCol !== null && entity[meterNumberCol]) {
      supNoPc.meterNumber = entity[meterNumberCol].trim();
    }
    if (zipCodeCol !== null && entity[zipCodeCol]) {  
      supNoPc.address.zipCode = entity[zipCodeCol].trim();
    }
    if (startDateCol !== null && entity[startDateCol]) {
      if (!moment.isDate(entity[startDateCol].trim())) {
        //return;
      }
      const date = moment.utc(entity[startDateCol].trim());
      supNoPc.newContractStartDate = date.toISOString();
    }
    if (keyCol !== null && entity[keyCol]) {
      if (utilInf.useNameKey === true) supNoPc.nameKey = entity[keyCol].trim();
      if (utilInf.useServiceNumber === true) supNoPc.serviceKey = entity[keyCol].trim();
    }
  } else {
    if (utilAccCol !== null && entity[utilAccCol]) {
      supNoPc.utilityAccountNumber = entity[utilAccCol].trim();
    }
    if (zipCodeCol !== null && entity[zipCodeCol]) { 
      supNoPc.address.zipCode = entity[zipCodeCol].trim();
    }

    if (startDateCol !== null && entity[startDateCol]) {
      if (!moment.isDate(entity[startDateCol].trim())) {
        //return;
      }
      const date = moment.utc(entity[startDateCol].trim());
      supNoPc.newContractStartDate = date.toISOString();
    }
  }

  return supNoPc;
  }
}

export class ClassValidation {
  public valid: boolean;
  public fields: { [k: string]: any } = {};

  constructor(values?: any) {
    this.valid = true;
    if (values) {
      Object.assign(this, values);
    }
  }

  public propValid(propName: string): boolean {
    return this.fields[propName]?.valid !== false;
  }

  public getAllValidationMessages(): string[] {
    let errMessages: string[] = [];
    Object.keys(this.fields).forEach((prop) => {
      Object.keys(this.fields[prop].errors).forEach((err) => {
        if(!errMessages.includes(this.fields[prop].errors[err])) errMessages = errMessages.concat([].concat(this.fields[prop].errors[err]));
      });
    });

    return errMessages;
  }
}

export class PropertyValidation {
  public valid: boolean;
  public errors: { [k: string]: string };

  constructor(values: any) {
    this.valid = true;
    Object.assign(this, values);
  }
}

export class PasteResult {
  public newRows: UtilityAccount[] = [];
  public duplicates: string[] = [];
  public get rowLimitExceeded() {
    return this.newRows.length > 100;
  }
  public get hasDuplicates() {
    return this.duplicates.length;
  }
}
